import React, { useCallback, useContext, useMemo } from 'react';

import { ConfluenceDependenciesContext } from '@atlassian/company-hub-common';
import type { ConfluenceDependenciesProviderProps } from '@atlassian/company-hub-common';

import {
	createRendererMediaProvider,
	createEditorMediaProvider,
	getAttachmentSizeLimitErrorFlag,
} from '@confluence/fabric-media-support';
import { ErrorBoundary, Attribution } from '@confluence/error-boundary';
import { ADVANCED_SEARCH, PEOPLE_DIRECTORY } from '@confluence/named-routes';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import {
	MacroExperienceStop,
	MacroExperienceStart,
	getMacroAttributesFromADFNode,
	getExperienceName,
	MacroExperienceSuccess,
} from '@confluence/macro-tracker';
import {
	ExperienceTimeout,
	CARDS_IMAGE_UPLOAD_EXPERIENCE,
	COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';
import { getMonitoringClient } from '@confluence/monitoring';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import type { FeedbackType } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { useFeedbackCollector } from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { ExtensionConfigUpsellLoader } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell';
import { ShortcutListener } from '@confluence/shortcuts';
import { getSavedThemeLocal } from '@confluence/theming/entry-points/getSavedThemeLocal';
import { useSearchSessionId } from '@confluence/search-session';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id';

import { usePremiumGate } from '../shared-components/usePremiumGate';
import { useGetSpaceIdsFromSpaceKeys } from '../shared-components/useGetSpaceIdsFromSpaceKeys';

import { useSegmentationTargetGroups } from './use-segmentation-target-groups/useSegmentationTargetGroups';
import { useAIDescription } from './use-ai-description/useAIDescription';

const useFeedbackCollectorWrapper = ({ source, type }: { source: string; type: FeedbackType }) => {
	return useFeedbackCollector({ source, type });
};

const useSegmentationTargetGroupsWrapper = ({
	nextCursor,
	initialGroupIds = [],
}: {
	initialGroupIds?: string[];
	nextCursor?: string;
}) => {
	return useSegmentationTargetGroups({ nextCursor, initialGroupIds });
};

const useAIDescriptionWrapper = ({
	spaceId,
	linkResourceType,
}: {
	spaceId: string;
	linkResourceType: string;
}) => {
	return useAIDescription({ spaceId, linkResourceType });
};

const ShortcutListenerWrapper = ({
	accelerator,
	listener,
}: {
	accelerator: string;
	listener: () => void;
}) => {
	return React.createElement(ShortcutListener, { accelerator, listener });
};

const ExtensionConfigUpsellLoaderWrapper = ({ feature }: { feature: string }) => {
	return React.createElement(ExtensionConfigUpsellLoader, {
		feature: feature as
			| 'premiumCardsConfig'
			| 'premiumCarouselConfig'
			| 'premiumSpotlightConfig'
			| 'premiumSearchConfig',
	});
};

const submitError = (error: Error) => {
	getMonitoringClient().submitError(error, {
		attribution: Attribution.COMPANY_HUB,
	});
	markErrorAsHandled(error);
};

export const useConfluenceMediaUploadExperience = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const start = useCallback(
		({ analyticsSource }: { analyticsSource: string }) => {
			experienceTracker.start({
				name: CARDS_IMAGE_UPLOAD_EXPERIENCE,
				attributes: {
					source: analyticsSource,
				},
			});
		},
		[experienceTracker],
	);

	const succeed = useCallback(() => {
		experienceTracker.succeed({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE });
	}, [experienceTracker]);

	const stopOnError = useCallback(
		({ error }: { error: Error }) => {
			experienceTracker.stopOnError({ name: CARDS_IMAGE_UPLOAD_EXPERIENCE, error });
		},
		[experienceTracker],
	);

	return {
		start,
		succeed,
		stopOnError,
	};
};

export const useConfluenceAIDescriptionExperience = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const start = useCallback(
		({
			analyticsSource,
			confluencePageId,
		}: {
			analyticsSource: string;
			confluencePageId: string;
		}) => {
			experienceTracker.start({
				name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE,
				attributes: {
					source: analyticsSource,
					contentId: confluencePageId,
				},
			});
		},
		[experienceTracker],
	);

	const succeed = useCallback(() => {
		experienceTracker.succeed({ name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE });
	}, [experienceTracker]);

	const stopOnError = useCallback(
		({ error }: { error: Error }) => {
			experienceTracker.stopOnError({ name: COMPANY_HUB_CARD_AI_DESCRIPTION_EXPERIENCE, error });
		},
		[experienceTracker],
	);

	return {
		start,
		succeed,
		stopOnError,
	};
};

export const ConfluenceDependenciesProviderComponent = ({
	mode,
	extensionNode,
	contentId = '',
	children,
	flags,
}: ConfluenceDependenciesProviderProps & WithFlagsProps) => {
	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys();
	const mediaUploadExperienceTracker = useConfluenceMediaUploadExperience();
	const aiDescriptionExperienceTracker = useConfluenceAIDescriptionExperience();
	const sessionData = useSessionData();
	const { cloudId, displayName, isAdminHubAIEnabled, orgId, userId } = sessionData || {};
	const [{ searchSessionId }] = useSearchSessionId();

	const currentDataRefHref = useRouteDataRef().current?.href;

	const experienceStartComponent = useCallback(() => {
		if (!extensionNode) return null;
		const macroAttributes = getMacroAttributesFromADFNode(extensionNode);
		const experienceName = getExperienceName(mode, extensionNode);

		return (
			<MacroExperienceStart
				name={experienceName}
				attributes={macroAttributes}
				extensionKey={extensionNode.extensionKey}
				contentId={contentId}
				mode={mode}
				timeout={ExperienceTimeout.MACRO_LOAD}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const experienceSuccessComponent = useCallback(() => {
		if (!extensionNode) return null;
		return (
			<MacroExperienceSuccess
				name={getExperienceName(mode, extensionNode)}
				contentId={contentId}
				extensionKey={extensionNode.extensionKey}
				mode={mode}
				attributes={getMacroAttributesFromADFNode(extensionNode)}
			/>
		);
	}, [extensionNode, contentId, mode]);

	const experienceStopComponent = useCallback(
		({ error }: { error?: Error }) => {
			if (!extensionNode) return null;
			return (
				<MacroExperienceStop
					error={error}
					name={getExperienceName(mode, extensionNode)}
					contentId={contentId}
					extensionKey={extensionNode.extensionKey}
					mode={mode}
					attributes={getMacroAttributesFromADFNode(extensionNode)}
				/>
			);
		},
		[mode, extensionNode, contentId],
	);

	const errorBoundaryComponent = useMemo(
		() =>
			({ children }: { children?: React.ReactNode }) => (
				<ErrorBoundary attribution={Attribution.COMPANY_HUB}>{children}</ErrorBoundary>
			),
		[],
	);

	const value = useMemo(
		() => ({
			experienceStartComponent,
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			searchSessionId: searchSessionId || '',
			displayName: displayName || '',
			isAdminHubAIEnabled,
			orgId: orgId || '',
			userId: userId || '',
			advancedSearchUrl: ADVANCED_SEARCH.toUrl(),
			peopleDirectoryUrl: PEOPLE_DIRECTORY.toUrl(),
			getSpaceIdsFromSpaceKeys,
			errorBoundaryComponent,
			createRendererMediaProvider,
			submitError,
			createEditorMediaProvider,
			getAttachmentSizeLimitErrorFlag,
			mediaUploadExperienceTracker,
			aiDescriptionExperienceTracker,
			currentDataRefHref,
			useFeedbackCollector: useFeedbackCollectorWrapper,
			lazyEmojiComponentLoader: LazyEmojiComponentLoader,
			usePremiumGate,
			shortcutListener: ShortcutListenerWrapper,
			ExtensionConfigUpsellLoader: ExtensionConfigUpsellLoaderWrapper,
			flags,
			useSegmentationTargetGroups: useSegmentationTargetGroupsWrapper,
			useAIDescription: useAIDescriptionWrapper,
			getSavedThemeLocal,
			getUniquePageLoadId,
		}),
		[
			experienceSuccessComponent,
			experienceStopComponent,
			cloudId,
			displayName,
			isAdminHubAIEnabled,
			orgId,
			userId,
			searchSessionId,
			errorBoundaryComponent,
			experienceStartComponent,
			getSpaceIdsFromSpaceKeys,
			mediaUploadExperienceTracker,
			aiDescriptionExperienceTracker,
			currentDataRefHref,
			flags,
		],
	);

	return (
		<ConfluenceDependenciesContext.Provider value={value}>
			{children}
		</ConfluenceDependenciesContext.Provider>
	);
};

export const ConfluenceDependenciesProvider = withFlags(
	({ children, ...props }: ConfluenceDependenciesProviderProps & WithFlagsProps) => (
		<ConfluenceDependenciesProviderComponent {...props}>
			{children}
		</ConfluenceDependenciesProviderComponent>
	),
);
